import React, {ReactElement, useEffect} from "react"
import {useNavigate} from "react-router-dom"
import {
	clearAchPayment,
	getExpectedCompletionTimeField,
	isAchPayment,
	isWireDrawdown,
	Payment,
	PaymentDirection,
} from "../../resources/payment"
import {kebabCase, startCase, upperCase} from "lodash"
import numeral from "numeral"
import {useAccessToken} from "../../services/auth"
import {useToasts} from "react-toast-notifications"
import useAsyncResultIdle from "../../hooks/useAsyncResultIdle"
import {ClearConfirmationModal, useCanClearAchPayment} from "./ClearAchPaymentHelper"
import {useModal} from "react-modal-hook"
import {Resource} from "../../resources/common"
import {AllowedPaymentColumns, PaymentColumns} from "./Payments.types"
import {DataTableCell, DataTableRow} from "../DataTable/DataTable"
import classNames from "classnames"
import {OrgName} from "../OrgName/OrgName"
import {BankName} from "../BankName/BankName"
import ReactTooltip from "react-tooltip"
import moment from "moment"
import {DataTableActionButton, DataTableActionItem} from "../DataTable/DataTableActionButton"
import IdLink from "../IdLink/IdLink"

interface PaymentRowProps {
	payment: Payment
	include?: Resource[]
	showOrgColumn?: boolean
	includedColumns: Array<AllowedPaymentColumns>
	refresh: () => void
	selectedPaymentId?: string
	onClick?: (e: React.MouseEvent<HTMLTableRowElement>) => void
}

export default function PaymentRow({
	payment,
	include,
	includedColumns,
	refresh,
	selectedPaymentId,
	onClick,
}: PaymentRowProps) {
	const navigate = useNavigate()
	const id = payment.id
	const org = payment.relationships.org?.data.id
	const bank = payment.relationships.bank?.data.id
	const typeCasingFunction = isAchPayment(payment) ? upperCase : startCase
	const type =
		isAchPayment(payment) && payment.attributes.direction === PaymentDirection.Debit
			? `${typeCasingFunction(payment.type.replace("Payment", ""))} ${payment.attributes.direction}`
			: typeCasingFunction(payment.type.replace("Payment", ""))
	const customerId = payment.relationships.customer?.data.id
	const accountId = payment.relationships.account.data.id
	const status = payment.attributes.status
	const reason = payment.attributes.reason
	const createdAt = payment.attributes.createdAt
	const amount = payment.attributes.amount
	const isWireDrawdownPayment = isWireDrawdown(payment)
	const accessToken = useAccessToken()
	const {addToast} = useToasts()
	const [state, clear] = useAsyncResultIdle(clearAchPayment)
	const canClearAchPayment = useCanClearAchPayment(payment, includedColumns.includes(PaymentColumns.actions))

	useEffect(() => {
		state.match(
			() => null,
			() => null,
			() => refresh(),
			(e) => addToast(e.errors[0].title)
		)
	}, [state])

	const [showClearingConfirmationModal, hideClearingConfirmationModal] = useModal(
		() => (
			<ClearConfirmationModal
				payment={payment}
				close={hideClearingConfirmationModal}
				onSubmit={() => {
					clear(accessToken, payment.id)
				}}
			/>
		),
		[]
	)

	const customer =
		customerId && include
			? include.find(
					(r: Resource) =>
						(r.type == "individualCustomer" || r.type == "businessCustomer" || r.type == "businessFBOCustomer") &&
						r.id == customerId.toString()
			  )
			: undefined
	let customerName: string | undefined = undefined

	if (customer) {
		if (customer.type == "individualCustomer") {
			customerName = `${customer.attributes.fullName.first} ${customer.attributes.fullName.last}`
		} else if (customer.type == "businessCustomer" || customer.type == "businessFBOCustomer") {
			customerName = customer.attributes.name
		}
	}

	const contentColumns: Partial<Record<AllowedPaymentColumns, ReactElement>> = {}

	if (includedColumns.includes(PaymentColumns.id)) {
		contentColumns["Id"] = (
			<DataTableCell className={classNames(id == selectedPaymentId && "selected-data-table-id", "data-table-id-cell")}>
				<IdLink className="data-table-id" href={`/payments/${id}`}>
					{id}
				</IdLink>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(PaymentColumns.org) && org) {
		contentColumns["Org"] = <OrgName orgId={org.toString()} included={include} />
	}

	if (includedColumns.includes(PaymentColumns.bank) && bank) {
		contentColumns["Bank"] = (
			<DataTableCell>
				<BankName bankId={bank.toString()} included={include} />
			</DataTableCell>
		)
	}

	if (includedColumns.includes(PaymentColumns.type)) {
		contentColumns["Type"] = (
			<DataTableCell>
				{isWireDrawdownPayment ? (
					<div className={"payment-row-type"}>
						{type}
						<span className="payment-row-type-secondary">Drawdown</span>
					</div>
				) : (
					type
				)}
			</DataTableCell>
		)
	}

	if (includedColumns.includes(PaymentColumns.customer)) {
		contentColumns["Customer"] = (
			<DataTableCell>
				<a
					className="link"
					onClick={(e) => {
						e.preventDefault()
						e.stopPropagation()
						if (customer) {
							navigate(`/${customer.type == "individualCustomer" ? "individual" : "business"}/${customerId}`)
						}
					}}
				>
					{customerName}
				</a>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(PaymentColumns.accountId)) {
		contentColumns["Account"] = (
			<DataTableCell>
				<a
					className="link"
					onClick={(e) => {
						e.preventDefault()
						e.stopPropagation()
						if (accountId) {
							navigate(`/accounts/${accountId}`)
						}
					}}
				>
					{accountId}
				</a>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(PaymentColumns.amount)) {
		contentColumns["Amount"] = (
			<DataTableCell>
				<span className="payments-amount">{numeral(amount / 100).format("$0,0.00")}</span>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(PaymentColumns.status)) {
		let tooltipValue: string | undefined = undefined
		if (reason) {
			tooltipValue = startCase(reason)
		}

		contentColumns["Status"] = (
			<DataTableCell>
				<div
					data-tip="React-tooltip"
					data-for={`status-reason-tooltip-${id}`}
					className={classNames("payments-status", kebabCase(status))}
				>
					{startCase(status)}

					<ReactTooltip
						className="tooltip-info"
						id={`status-reason-tooltip-${id}`}
						disable={!tooltipValue}
						effect="solid"
					>
						{tooltipValue}
					</ReactTooltip>
				</div>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(PaymentColumns.createdAt)) {
		contentColumns["Created At"] = <DataTableCell>{moment(createdAt).format("L LT")}</DataTableCell>
	}

	if (includedColumns.includes(PaymentColumns.expectedCompletionDate)) {
		const creditExpectedCompletionTimeTooltipValue =
			"The funds from ACH credits that are originated before the daily cutoff, are received by Unit and released to the customer on the next business day"
		const debitExpectedCompletionTimeTooltipValue =
			"The funds from ACH debits that are originated before the daily cutoff, are received by Unit on the next business day, and held for the clearing period defined on the account’s deposit product"

		let expectedCompletionTimeValue = "-"
		let showToolTip = false

		let expectedCompletionTimeField: Date | undefined = undefined
		if (isAchPayment(payment)) {
			expectedCompletionTimeField = getExpectedCompletionTimeField(payment)
			if (expectedCompletionTimeField) {
				expectedCompletionTimeValue = moment(expectedCompletionTimeField).format("MM/DD/YYYY")
				showToolTip = true
			}
		}

		let expectedCompletionTimeTooltipValue: string | undefined = undefined
		if (isAchPayment(payment) && showToolTip) {
			if (payment.attributes.direction == "Credit") {
				expectedCompletionTimeTooltipValue = creditExpectedCompletionTimeTooltipValue
			} else {
				expectedCompletionTimeTooltipValue = debitExpectedCompletionTimeTooltipValue
			}
		}

		contentColumns["Expected Completion Date"] = (
			<DataTableCell>
				<span className="is-flex is-align-items-center ml-1">
					<span data-tip="React-tooltip" data-for={`payment-expected-completion-time-tooltip-${id}`}>
						{expectedCompletionTimeValue}
					</span>
					<ReactTooltip
						className="tooltip-info"
						id={`payment-expected-completion-time-tooltip-${id}`}
						disable={!showToolTip}
						effect="solid"
					>
						{expectedCompletionTimeTooltipValue}
					</ReactTooltip>
				</span>
			</DataTableCell>
		)
	}

	if (includedColumns.includes(PaymentColumns.actions)) {
		contentColumns["Actions"] = (
			<DataTableActionButton enableActions={canClearAchPayment}>
				{canClearAchPayment ? (
					<>
						<DataTableActionItem
							title={"Clear Payment"}
							icon={"money-fund-move-found--business-products"}
							onClick={() => showClearingConfirmationModal()}
						/>
					</>
				) : null}
			</DataTableActionButton>
		)
	}

	if (includedColumns.includes(PaymentColumns.reason)) {
		contentColumns["Reason"] = <DataTableCell>{reason ? startCase(reason) : " - "}</DataTableCell>
	}

	const content: Array<ReactElement | null> = []

	includedColumns.forEach((col: AllowedPaymentColumns, i: number) => {
		if (col in contentColumns && contentColumns[col]) {
			const column = {...contentColumns[col]} as ReactElement
			column.key = i.toString()
			content.push(column)
		}
	})

	return (
		<DataTableRow
			className={classNames(id === selectedPaymentId && "selected-data-table-row")}
			onClick={(e) => onClick && onClick(e)}
		>
			{content}
		</DataTableRow>
	)
}
